import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { actionsApi, type ActionItem } from '../api/modules';
import { useToast } from '../components/toast/Toast';

const META: Record<ActionItem['priority'], { label: string; border: string; bar: string; badge: string }> = {
  critical: { label: 'Critical · Today',   border: 'border-red-200',   bar: 'bg-red-500',   badge: 'bg-red-100 text-red-700' },
  high:     { label: 'High priority',      border: 'border-amber-200', bar: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700' },
  medium:   { label: 'Medium priority',    border: 'border-cream-200', bar: 'bg-navy-500',  badge: 'bg-navy-100 text-navy-800' },
  low:      { label: 'Low priority',       border: 'border-cream-200', bar: 'bg-cream-300', badge: 'bg-cream-100 text-navy-800/70' },
};

export function ActionDuePage() {
  const navigate = useNavigate();
  const { show } = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['actions-due'],
    queryFn: actionsApi.due,
    refetchInterval: 60_000,
  });

  return (
    <>
      <Header breadcrumbs={['Overview', 'Action Due']} />
      <div className="px-8 pt-7 pb-2">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-navy-800/50 mb-1">Personalized task queue</div>
            <h1 className="serif-display text-3xl text-navy-800 font-medium">Action Due</h1>
            <p className="text-sm text-navy-800/60 mt-1">Items requiring your decision · ordered by priority and SLA</p>
          </div>
        </div>
      </div>

      <div className="px-8 py-5 space-y-5 overflow-y-auto">
        {/* Summary strip */}
        <div className="grid grid-cols-4 gap-3">
          {(['critical', 'high', 'medium', 'low'] as const).map((p) => {
            const m = META[p];
            const count = data?.summary[p] ?? 0;
            return (
              <div key={p} className={`bg-white border ${m.border} p-4 rounded-sm relative`}>
                <div className={`absolute top-0 left-0 w-1 h-full ${m.bar}`} />
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50 ml-2">{m.label}</div>
                <div className="font-numeric serif-display text-2xl text-navy-800 font-medium mt-1 ml-2">
                  {isLoading ? '…' : count}
                </div>
                <div className="text-[11px] text-navy-800/60 mt-1 ml-2">
                  {count === 1 ? 'pending action' : 'pending actions'}
                </div>
              </div>
            );
          })}
        </div>

        {/* Action list */}
        {isLoading || !data ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-24 bg-cream-100 rounded-sm animate-pulse" />
            ))}
          </div>
        ) : data.actions.length === 0 ? (
          <div className="bg-white border border-cream-200 rounded-sm p-12 text-center">
            <div className="text-4xl mb-2">✓</div>
            <div className="serif-display text-xl text-navy-800">Inbox zero</div>
            <p className="text-sm text-navy-800/60 mt-1">No pending actions. The AI engine will surface new items as they arise.</p>
          </div>
        ) : (
          (['critical', 'high', 'medium', 'low'] as const).map((p) => {
            const items = data.actions.filter((a) => a.priority === p);
            if (items.length === 0) return null;
            const m = META[p];
            return (
              <section key={p}>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="serif-display text-base text-navy-800 font-medium">{m.label}</h3>
                  <span className={`text-[10px] ${m.badge} px-2 py-0.5 rounded font-medium font-numeric`}>{items.length}</span>
                </div>
                <div className="space-y-2.5">
                  {items.map((a) => (
                    <div
                      key={a.id}
                      onClick={() => navigate(`/${a.link}`)}
                      className={`bg-white border ${m.border} rounded-sm p-4 hover:shadow-md transition cursor-pointer relative`}
                    >
                      <div className={`absolute top-0 left-0 w-1 h-full ${m.bar}`} />
                      <div className="ml-2 flex items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="text-[10px] uppercase tracking-wider font-medium text-navy-800/50">{a.type}</span>
                            <span className="text-[10px] text-navy-800/40 font-numeric">{a.module} · {a.id}</span>
                          </div>
                          <div className="text-sm font-medium text-navy-800 leading-snug">{a.title}</div>
                          <div className="text-xs text-navy-800/70 mt-1.5 leading-relaxed">{a.desc}</div>
                          <div className="text-[11px] text-navy-800/50 mt-2 font-numeric flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
                              </svg>
                              <span className={a.overdue ? 'text-red-600 font-medium' : ''}>{a.deadline}</span>
                            </span>
                            <span>·</span>
                            <span>{a.submitter}</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {a.count > 1 && (
                            <>
                              <div className="font-numeric serif-display text-3xl text-navy-800 font-medium leading-none">{a.count}</div>
                              <div className="text-[10px] text-navy-800/50 uppercase tracking-wider mt-1">items</div>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/${a.link}`); show(`Opening ${a.link}`); }}
                            className="mt-3 px-3 py-1.5 text-xs bg-saffron-500 hover:bg-saffron-600 text-white rounded-sm transition"
                          >
                            Open →
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })
        )}
      </div>
    </>
  );
}
